import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { twMerge } from 'tailwind-merge';

import { hiddenRichTextTags } from 'utils/question-label';

import { RichTextPreviewProps } from './types';

// Use an isolated instance so link hooks do not affect other DOMPurify callers.
const purifier = DOMPurify();
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (
    node.nodeName.toLowerCase() !== 'a' ||
    node.getAttribute('target')?.toLowerCase() !== '_blank'
  )
    return;
  const rel = new Set(
    (node.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean)
  );
  rel.add('noopener');
  rel.add('noreferrer');
  node.setAttribute('rel', Array.from(rel).join(' '));
});

const richTextStyles = [
  // Base styles (exclude headings so they use global styles)
  '[&_*:not(h1,h2,h3,h4,h5,h6)]:[font-size:inherit]',
  '[&_*:not(h1,h2,h3,h4,h5,h6)]:[line-height:inherit]',
  // Heading descendants inherit typography instead of the global body reset.
  '[&_h1_*:not(strong,b)]:[font-weight:inherit]',
  '[&_h2_*:not(strong,b)]:[font-weight:inherit]',
  '[&_h3_*:not(strong,b)]:[font-weight:inherit]',
  '[&_h4_*:not(strong,b)]:[font-weight:inherit]',
  '[&_h5_*:not(strong,b)]:[font-weight:inherit]',
  '[&_h6_*:not(strong,b)]:[font-weight:inherit]',
  // Paragraphs
  '[&_p]:min-h-5',
  // Text formatting
  '[&_strong]:font-bold',
  '[&_strong_*]:font-bold',
  '[&_b]:font-bold',
  '[&_b_*]:font-bold',
  // Links
  '[&_a]:text-blue-primary',
  '[&_a]:hover:text-blue-hover',
  '[&_a]:active:text-blue-active',
  // Lists - base
  '[&_ol]:pl-6',
  '[&_ul]:pl-6',
  // Ordered lists - nested styles
  '[&_ol]:list-decimal',
  '[&_ol_ol]:list-[lower-alpha]',
  '[&_ol_ol_ol]:list-[lower-roman]',
  '[&_ol_ol_ol_ol]:list-decimal',
  '[&_ol_ol_ol_ol_ol]:list-[lower-alpha]',
  '[&_ol_ol_ol_ol_ol_ol]:list-[lower-roman]',
  // Unordered lists - nested styles
  '[&_ul]:list-disc',
  '[&_ul_ul]:list-[circle]',
  '[&_ul_ul_ul]:list-[square]',
  '[&_ul_ul_ul_ul]:list-disc',
  '[&_ul_ul_ul_ul_ul]:list-[circle]',
  '[&_ul_ul_ul_ul_ul_ul]:list-[square]',
  // Images - inline-block to respect text-align from parent div
  '[&_img]:inline-block',
  '[&_img]:align-top',
].join(' ');

export const RichTextPreview = ({
  content,
  className,
  inline = false,
  allowImages = true,
}: RichTextPreviewProps) => {
  const sanitizedHtml = useMemo(() => {
    if (!content) return '';
    // Remove hidden content and enforce safe link attributes during the final sanitize pass.
    return purifier.sanitize(content, {
      ADD_ATTR: ['target'],
      RETURN_TRUSTED_TYPE: false,
      ADD_FORBID_CONTENTS: hiddenRichTextTags,
      FORBID_TAGS: [
        ...hiddenRichTextTags,
        ...(!allowImages
          ? ['img', 'picture', 'svg', 'image', 'video', 'audio']
          : []),
      ],
    });
  }, [content, allowImages]);

  if (!content) return null;

  // Rich text contains flow content; inline changes layout, not HTML semantics.
  return (
    <div
      className={twMerge(richTextStyles, className)}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      style={
        inline ? { whiteSpace: 'inherit', display: 'inline-block' } : undefined
      }
    />
  );
};
